import React, { memo, useState, useEffect, useRef, useCallback } from 'react';

import { Button } from 'src/components/button';

import * as S from './styles';
import { Grow, Variant } from './types';

type Props = {
  className?: string;
  children: React.ReactElement | Array<React.ReactElement | null>;
  grow?: Grow;
  toggleElement: React.ReactElement | string;
  showArrow?: boolean;
  variant?: Variant;
  onSelect?: (key?: React.Key) => void;
  'data-test'?: string;
};

const Dropdown = (props: Props) => {
  const [expanded, setExpandedState] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    className,
    children,
    grow = 'none',
    toggleElement,
    showArrow = true,
    variant = 'primary',
    onSelect,
  } = props;

  const toggleDropdown = useCallback(() => {
    setExpandedState(!expanded);
  }, [expanded]);

  const onToggleElementClick = useCallback(
    (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      if (typeof toggleElement !== 'string' && toggleElement.props.onClick) {
        toggleElement.props.onClick(e);
      } else {
        e.preventDefault();
        e.stopPropagation();
        toggleDropdown();
      }
    },
    [toggleDropdown, toggleElement]
  );

  const handleClickOutside = (e: Event) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(e.target as Node)
    ) {
      setExpandedState(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  return (
    <S.Dropdown
      variant={variant}
      expanded={expanded}
      ref={dropdownRef}
      className={className}
      data-test={props['data-test']}
    >
      {typeof toggleElement === 'string' ? (
        <Button
          style={S.sharedButtonStyles}
          variant="text"
          color="link"
          onClick={toggleDropdown}
          iconPosition="right"
          icon={showArrow ? <S.Arrow expanded={expanded ? 1 : 0} /> : null}
          data-test="DropdownToggle"
        >
          {toggleElement}
        </Button>
      ) : (
        <toggleElement.type
          style={S.sharedButtonStyles}
          data-test="DropdownToggle"
          {...toggleElement.props}
          onClick={onToggleElementClick}
          className="dropdown-toggle-element"
        >
          {toggleElement.props.children}{' '}
          {showArrow && <S.Arrow expanded={expanded ? 1 : 0} />}
        </toggleElement.type>
      )}
      <S.DropdownHideable visible={expanded}>
        <S.DropdownContent variant={variant} expanded={expanded} grow={grow}>
          {React.Children.map(children, (child?: React.ReactElement | null) => {
            if (!child) {
              return null;
            }

            return React.cloneElement(child, {
              ...child.props,
              onClick(e: React.MouseEvent) {
                if (child.props.onClick) {
                  child.props.onClick(e);
                }
                if (onSelect) {
                  onSelect(child.key || undefined);
                }
                if (child.props.to || child.props.onClick || onSelect) {
                  toggleDropdown();
                }
              },
              'data-test': 'DropdownOption',
              'data-test-key': child.key,
            });
          })}
        </S.DropdownContent>
      </S.DropdownHideable>
    </S.Dropdown>
  );
};

export const DropdownNew = memo(Dropdown);
